import { DefaultNoRowsOverlay } from './Components/NoRowsOverlay';
import { CellConfiguration } from './Components/Cells/CellLoader';
import {
  DataGridProProps,
  GridApiPro,
  GridColDef,
  gridDimensionsSelector,
  GridPaginationModel,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { usePrepareConfiguration } from './hooks/usePrepareConfiguration';

import { SecondaryColumnHeaders } from './Components/Rows/SecondaryColumnHeaders';
import { BaseGrid } from './Components/Base/BaseGrid';
import { useDataGridDialogs } from './hooks/useDataGridDialogs';
import React, { useEffect, useState } from 'react';
import { ColumnConfiguration, HeaderAction } from './models/configurableTable';
import { DefaultLoadingOverlay } from './Components/Loaders/DefaultLoadingOverlay';
import { DefaultColumnHeaderSortIcon } from './Components/Header/DefaultColumnSortIcon';
import { pxToRem } from 'src/common/utils/fonts';

//To remove when use this component in SimInventory
const CustomizeMuiDataGrid = styled(BaseGrid)(({ theme }) => ({
  '& .MuiDataGrid-skeletonHeaderRow': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiDataGrid-container--top::after': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiDataGrid-columnHeader': {
    borderBottom: 'none',
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: theme.palette.background.paper,
    '& > *': {
      backgroundColor: `${theme.palette.background.paper} !important`,
    },
  },
  '& .MuiDataGrid-row.MuiDataGrid-row--lastVisible': {
    '& .MuiDataGrid-cell': {
      borderBottom: `none`,
    },
  },
  '& .MuiDataGrid-bottomContainer': {
    border: 'none',
    height: 0,
  },
  '& .MuiDataGrid-columnHeader--pinnedRight': {
    backgroundColor: `${theme.palette.background.paper} !important`,
  },
}));

type SubGridHeaderActionsProps = {
  actionColDef: GridColDef;

  subgridApi: React.RefObject<GridApiPro>;
};

const SubGridHeaderActions = ({ actionColDef, subgridApi }: SubGridHeaderActionsProps) => {
  const mainGridRef = useGridApiContext();
  const { root: visibleViewport } = useGridSelector(mainGridRef, gridDimensionsSelector);

  if (!actionColDef?.renderHeader || !subgridApi.current) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 30,
        right: 30,
        zIndex: 1000,
        pointerEvents: 'none',
        height: pxToRem(42),
      }}
    >
      <Box
        className="headerAction"
        sx={{
          position: 'sticky',
          left: visibleViewport.width - (actionColDef.width || 50),
          height: pxToRem(42),
          width: actionColDef.width || 50,
          pointerEvents: 'all',
          opacity: 0,
          '&:hover': {
            opacity: 1,
          },
        }}
      >
        {actionColDef?.renderHeader(subgridApi.current.getColumnHeaderParams('actions'))}
      </Box>
    </Box>
  );
};

type SubGridAdditionalActionsProps = {
  rows?: Record<string, any>[];
  getRowId?: DataGridProProps['getRowId'];
  subgridApi: React.RefObject<GridApiPro>;
  actionColDef: GridColDef;
};

const SubGridAdditionalActions = ({
  rows = [],
  getRowId,
  subgridApi,
  actionColDef,
}: SubGridAdditionalActionsProps) => {
  const mainGridRef = useGridApiContext();
  const { root: visibleViewport } = useGridSelector(mainGridRef, gridDimensionsSelector);

  const dims = subgridApi.current?.getRootDimensions
    ? subgridApi.current?.getRootDimensions()
    : null;

  const headersTotalHeight = dims?.headersTotalHeight || 0;
  const rowsCount = rows?.length || 0;
  const rowHeight = dims?.rowHeight || 0;

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 30,
        right: 30,
        zIndex: 1000,
        pointerEvents: 'none',
        marginTop: `${headersTotalHeight}px`,
        height: rowsCount * rowHeight,
      }}
    >
      {rows?.map((row: any) => {
        const rowId = getRowId ? getRowId(row) : row.id;
        return (
          <Box
            key={rowId}
            data-id={rowId}
            sx={{
              position: 'sticky',
              left: visibleViewport.width - (actionColDef.width || 50),
              height: `${rowHeight}px`,
              width: actionColDef.width || 50,
              pointerEvents: 'all',
              opacity: 0,
            }}
          >
            <Box
              sx={{
                width: '100%',

                height: `${rowHeight}px`,
              }}
            >
              <Box
                sx={{
                  background: ({ palette }) => palette.background.default,
                  overflow: 'hidden',
                  marginTop: '0.5px', //IT WORKS DON"t KNOW WHY,
                  height: 50,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {/* @ts-expect-error -- missing properties for renderCell */}
                {actionColDef.renderCell?.({ row })}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

type ConfigurableTableProps<T> = {
  disablePagination?: boolean;
  disableColumnReorder?: boolean;
  configuration: ColumnConfiguration[];
  cellConfiguration: CellConfiguration;
  cellProps?: any;
  //TODO: Imporve typings (generic)
  rows?: Record<string, any>[];
  totalNumberOfItems?: number;
  actions?: any;
  headerActions?: HeaderAction<T>[];
  'data-testid'?: string;
  page?: number;
  pageSize?: number;
  onPaginationChange?: (pagination: GridPaginationModel) => void;
  slots?: DataGridProProps['slots'];

  loading?: DataGridProProps['loading'];
  getRowId?: DataGridProProps['getRowId'];
  onSortModelChange?: DataGridProProps['onSortModelChange'];
  initialState?: DataGridProProps['initialState'];
};

export const SecondaryConfigurableTable = <T,>({
  slots,
  rows,
  configuration,
  cellConfiguration,
  cellProps,
  actions,
  headerActions,
  'data-testid': testid,
  ...props
}: ConfigurableTableProps<T>) => {
  const apiRef = useGridApiRef();

  //need force rerender to have apiRef initialized (problem on prod build only -> dev mode components rerender twice)
  const [, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const { dialogs, selectDialog, unselectDialog, dialogActions } = useDataGridDialogs({
    actions,
    headerActions,
  });

  const columnsConfiguration = usePrepareConfiguration({
    detailPanelEnabled: false,
    configuration,
    cellConfiguration,
    cellProps,
    actions,
    headerActions,
    selectDialog,
  });

  const actionColDef = columnsConfiguration.find((col) => col.field === 'actions');
  const actionsSx = actionColDef
    ? rows?.reduce((currentStyle, row) => {
        const rowId = props.getRowId ? props.getRowId(row) : row.id;
        return {
          ...currentStyle,
          [`&:has(div[data-id="${rowId}"]:hover) div[data-id="${rowId}"]`]: {
            opacity: 1,
          },
          [`&:has(.MuiDataGrid-columnHeaders:hover) .headerAction`]: {
            opacity: 1,
          },
        };
      }, {})
    : undefined;

  const filterdColumnsConfiguration = actionColDef
    ? columnsConfiguration.filter((col) => col.field !== 'actions')
    : columnsConfiguration;

  const hasAdditionalAction = actionColDef && apiRef.current;
  const hasHeaderAdditionalAction = !!actionColDef?.renderHeader;
  return (
    <>
      <Box sx={actionsSx}>
        {hasHeaderAdditionalAction && (
          <SubGridHeaderActions
            actionColDef={actionColDef}
            subgridApi={apiRef as React.RefObject<GridApiPro>}
          />
        )}
        {hasAdditionalAction && (
          <SubGridAdditionalActions
            rows={rows}
            getRowId={props.getRowId}
            subgridApi={apiRef as React.RefObject<GridApiPro>}
            actionColDef={actionColDef}
          />
        )}
        <CustomizeMuiDataGrid
          {...props}
          slotProps={{
            root: {
              'data-testid': testid,
            },
          }}
          disablePagination
          apiRef={apiRef}
          columns={filterdColumnsConfiguration}
          slots={{
            columnHeaderSortIcon: DefaultColumnHeaderSortIcon,
            noRowsOverlay: DefaultNoRowsOverlay,
            loadingOverlay: DefaultLoadingOverlay,
            row: undefined,
            columnHeaders: SecondaryColumnHeaders,
            ...slots,
          }}
          rows={rows}
        />
      </Box>
      {dialogActions.map((action: any) => {
        return (
          <React.Fragment key={action.id}>
            <action.Component
              open={!!dialogs[action.id]}
              data={dialogs[action.id]}
              onClose={() => {
                unselectDialog(action.id);
              }}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};
